/**
 * Реферальная ссылка друга (`?ref=<handle>`) — работает на ЛЮБУЮ покупку.
 *
 * Владелец, 28.09.2026: «реферальные ссылки должны автоматически работать
 * если человек решит купить сразу комбо вместо одного продукта на который
 * дана ссылка».
 *
 * Как было: handle жил в sessionStorage страницы одной части курса
 * (`affiliateHandle:<courseId>`) и уходил только с оплатой этой части; вкладку
 * закрыли — ссылки нет.
 *
 * Как стало:
 *  - `?ref=` ловится на ЛЮБОЙ странице сайта (App.tsx, рядом с utm) и
 *    запоминается на 30 дней в localStorage — плюс в sessionStorage, если
 *    localStorage недоступен (как раньше, в пределах вкладки);
 *  - последний клик побеждает: новая ссылка перезаписывает старую;
 *  - `getReferralHandle()` отдаёт handle любой оплате — часть курса, пакет,
 *    год приложения, токены, специалист, подарок, диагностика; сервер сам
 *    решает, кому засчитать (site_server services_fastapi/referral.py), и
 *    молча игнорирует неизвестный handle.
 *
 * Форма handle — та же, что у воркера (AFFILIATE_HANDLE_REGEX): чужой
 * `?ref=producthunt!` не сохраняется вовсе.
 *
 * Владелец 03.10: друг, пришедший по ссылке, приносит пригласившему баллы и
 * без покупки — поставил приложение (+2 000), начал заниматься (+3 000).
 * Для этого воркеру надо знать, что человек пришёл по ссылке:
 * features/affiliate/model/referralArrival берёт у воркера метку перехода
 * (`click`, хранится здесь же) и после входа сообщает POST /affiliate/arrive.
 */
const STORAGE_KEY = "referral_handle";
const SESSION_KEY = "referral_handle";
/** Прежний ключ страницы покупки части — читаем, чтобы не потерять уже открытые вкладки. */
const LEGACY_SESSION_PREFIX = "affiliateHandle:";
export const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const HANDLE_RE = /^[a-z][a-z0-9-]{2,19}$/;

export interface StoredReferral {
    handle: string;
    capturedAt: number;
    /**
     * Метка перехода, подписанная воркером (POST /affiliate/click, «мс.подпись»):
     * по ней воркер засчитывает приход по ссылке с мига перехода — часы
     * браузера он двое суток назад не принимает (features/affiliate/model/referralArrival).
     */
    click?: string;
}

function readStored(): StoredReferral | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as StoredReferral | null) : null;
    } catch {
        return null;
    }
}

/** Нормализованный handle или null, если он не похож на handle. */
export function normalizeReferralHandle(raw: unknown): string | null {
    const h = String(raw ?? "").trim().toLowerCase();
    return HANDLE_RE.test(h) ? h : null;
}

function readSearch(search?: string): string {
    if (typeof search === "string") return search;
    try {
        return window.location.search || "";
    } catch {
        return "";
    }
}

/**
 * Прочитать `?ref=` из адреса и запомнить на 30 дней. Возвращает handle из
 * адреса или null (в адресе ссылки нет / она кривая).
 */
export function captureReferralFromUrl(search?: string, now: number = Date.now()): string | null {
    let handle: string | null = null;
    try {
        handle = normalizeReferralHandle(new URLSearchParams(readSearch(search)).get("ref"));
    } catch {
        return null;
    }
    if (!handle) return null;
    // Та же ссылка ещё раз — метка первого перехода остаётся (она раньше и верна).
    const prev = readStored();
    const keepClick = prev && prev.handle === handle && typeof prev.click === "string" ? prev.click : undefined;
    const payload: StoredReferral = keepClick ? { handle, capturedAt: now, click: keepClick } : { handle, capturedAt: now };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        /* localStorage недоступен — остаётся вкладка */
    }
    try {
        sessionStorage.setItem(SESSION_KEY, handle);
    } catch {
        /* sessionStorage недоступен — ссылка только в этом адресе */
    }
    return handle;
}

/** Handle из localStorage, если ему не больше 30 дней (просроченный стирается). */
function storedHandle(now: number): string | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredReferral | null;
        const handle = normalizeReferralHandle(parsed?.handle);
        const at = parsed?.capturedAt;
        if (!handle || typeof at !== "number" || !Number.isFinite(at)) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        if (now - at > REFERRAL_TTL_MS) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return handle;
    } catch {
        return null;
    }
}

function sessionHandle(courseId?: string): string | null {
    try {
        const own = normalizeReferralHandle(sessionStorage.getItem(SESSION_KEY));
        if (own) return own;
        if (courseId) {
            return normalizeReferralHandle(
                sessionStorage.getItem(`${LEGACY_SESSION_PREFIX}${courseId}`),
            );
        }
    } catch {
        /* sessionStorage недоступен */
    }
    return null;
}

/**
 * Handle для оплаты: из текущего адреса (и сразу запомнить), иначе
 * запомненный за последние 30 дней, иначе из вкладки. null — ссылки нет.
 * `courseId` — только чтобы подобрать ключ прежней страницы покупки части.
 */
export function getReferralHandle(opts: { courseId?: string; now?: number } = {}): string | null {
    const now = opts.now ?? Date.now();
    return captureReferralFromUrl(undefined, now) ?? storedHandle(now) ?? sessionHandle(opts.courseId);
}

/**
 * Тот же handle, что у `getReferralHandle()` (адрес страницы → запомненный за
 * 30 дней → вкладка), но адрес страницы не запоминается — для ссылок, которые
 * строятся при отрисовке (Google Play: shared/lib/playStoreLink.ts). Запоминает
 * адрес App; ссылка, отрисованная раньше его эффекта, уже берёт handle из адреса.
 */
export function peekReferralHandle(now: number = Date.now()): string | null {
    let fromUrl: string | null = null;
    try {
        fromUrl = normalizeReferralHandle(new URLSearchParams(readSearch()).get("ref"));
    } catch {
        fromUrl = null;
    }
    return fromUrl ?? storedHandle(now) ?? sessionHandle();
}

/**
 * Запомненная ссылка целиком ({handle, capturedAt, click?}), если ей не больше
 * 30 дней, — для сообщения воркеру «пришёл по ссылке». Только localStorage:
 * ссылка из вкладки без даты перехода приходом не считается.
 */
export function getStoredReferral(now: number = Date.now()): StoredReferral | null {
    const handle = storedHandle(now);
    if (!handle) return null;
    const rec = readStored();
    if (!rec || rec.handle !== handle) return null;
    return typeof rec.click === "string"
        ? { handle, capturedAt: rec.capturedAt, click: rec.click }
        : { handle, capturedAt: rec.capturedAt };
}

/** Положить подписанную воркером метку перехода к запомненной ссылке (той же). */
export function setReferralClick(handle: string, click: string): boolean {
    const rec = readStored();
    if (!rec || rec.handle !== handle || !click) return false;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...rec, click }));
        return true;
    } catch {
        return false;
    }
}

/** `{ affiliateHandle }` для тела запроса — или пустой объект, если ссылки нет. */
export function referralBody(opts: { courseId?: string } = {}): { affiliateHandle?: string } {
    const handle = getReferralHandle(opts);
    return handle ? { affiliateHandle: handle } : {};
}
